import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompany } from '@/contexts/CompanyContext';
import { useAdminData } from '@/hooks/useAdminData';
import { useAdminFilters } from '@/hooks/useAdminFilters';
import { usePagination } from '@/hooks/usePagination';
import { useDashboardState } from '@/hooks/useDashboardState';
import { useDashboardActions } from '@/hooks/useDashboardActions';
import { useCompanyAwareQueries } from '@/hooks/useCompanyAwareQueries';

export const useDashboard = () => {
  const { isAdmin, user } = useAuth();
  const { subscribed } = useSubscription();
  const { currentCompany, loading: companyLoading } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Read position filter from URL params
  const urlParams = new URLSearchParams(location.search);
  const initialPosition = urlParams.get('position') || undefined;
  
  // Track initial load to prevent loading skeleton on search/filter changes
  const isInitialLoad = useRef(true);
  
  // Company-aware cache management
  useCompanyAwareQueries();
  
  // State management
  const dashboardState = useDashboardState();
  
  // Data hooks
  const adminData = useAdminData();
  const adminFilters = useAdminFilters(initialPosition);
  
  const behavioralPagination = usePagination({ totalItems: adminData.filteredBehavioralCount });
  const workBehaviorPagination = usePagination({ totalItems: adminData.filteredWorkBehaviorCount });
  const profilesPagination = usePagination({ totalItems: adminData.totalCounts.idealProfiles });

  // Actions
  const dashboardActions = useDashboardActions(adminData, adminFilters, behavioralPagination);
  
  // Handle position URL parameter - set active tab to behavioral
  useEffect(() => {
    if (initialPosition) {
      dashboardState.setActiveTab('behavioral');
      // Clear URL params after reading
      navigate(location.pathname, { replace: true });
    }
  }, []);

  // Handle navigation state for setting active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      console.log('Dashboard: Setting active tab from navigation state:', location.state.activeTab);
      dashboardState.setActiveTab(location.state.activeTab);
      // Clear the state to prevent it from persisting
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, dashboardState.setActiveTab, navigate, location.pathname]);

  // Reset initial load flag when company changes to show loading skeleton
  useEffect(() => {
    isInitialLoad.current = true;
  }, [currentCompany?.company_id]);

  // Fetch all data when dependencies change (includes total counts)
  useEffect(() => {
    // Guard: Wait for company context to be ready
    if (companyLoading) {
      return;
    }

    // Set search loading state IMMEDIATELY (synchronously) to prevent blink
    // This eliminates the gap between debounce completing and async fetch starting
    if (!isInitialLoad.current) {
      adminData.setFetchingSearch(true);
    }

    const fetchData = async () => {
      try {
        // Only show loading skeleton on initial page load
        if (isInitialLoad.current) {
          adminData.setLoading(true);
        }
        
        // Fetch all data including counts
        await Promise.all([
          adminData.fetchTotalCounts(),
          adminData.fetchBehavioralData(
            behavioralPagination.currentPage,
            behavioralPagination.pageSize,
            adminFilters.debouncedSearchTerm,
            adminFilters.currentFilters
          ),
          adminData.fetchWorkBehaviorData(
            workBehaviorPagination.currentPage,
            workBehaviorPagination.pageSize,
            adminFilters.debouncedSearchTerm,
            adminFilters.currentFilters
          ),
          adminData.fetchIdealProfilesData(
            profilesPagination.currentPage,
            profilesPagination.pageSize
          )
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        // Always reset loading state after fetch completes
        adminData.setLoading(false);
        isInitialLoad.current = false;
      }
    };

    fetchData();
  }, [
    companyLoading,
    currentCompany?.company_id,
    adminFilters.debouncedSearchTerm,
    adminFilters.currentFilters,
    behavioralPagination.currentPage,
    behavioralPagination.pageSize,
    workBehaviorPagination.currentPage,
    workBehaviorPagination.pageSize,
    profilesPagination.currentPage,
    profilesPagination.pageSize
  ]);

  const getPageTitle = () => {
    switch (dashboardState.activeTab) {
      case 'dashboard':
        return 'Dashboard';
      case 'behavioral':
        return 'Candidates';
      case 'profiles':
        return 'Ideal Profiles';
      case 'comparison':
        return 'Talent Optimization';
      case 'workBehavior':
        return 'Work Behavior';
      case 'userProfiles':
        return 'Standard Profiles';
      case 'registration':
        return 'Registration Link';
      case 'settings':
        return 'Settings';
      default:
        return 'Dashboard';
    }
  };

  const handleManageSubscription = () => {
    navigate('/pricing');
  };

  return {
    // State
    activeTab: dashboardState.activeTab,
    setActiveTab: dashboardState.setActiveTab,
    filtersExpanded: dashboardState.filtersExpanded,
    setFiltersExpanded: dashboardState.setFiltersExpanded,
    tableExpanded: dashboardState.tableExpanded,
    setTableExpanded: dashboardState.setTableExpanded,
    selectedIdealProfile: dashboardState.selectedIdealProfile,
    setSelectedIdealProfile: dashboardState.setSelectedIdealProfile,
    handleToggleTable: dashboardState.handleToggleTable,
    handleToggleFilters: dashboardState.handleToggleFilters,
    
    // Data
    loading: companyLoading || adminData.loading,
    dataLoadError: adminData.dataLoadError,
    totalCounts: adminData.totalCounts,
    behavioralSubmissions: adminData.behavioralSubmissions,
    workBehaviorSubmissions: adminData.workBehaviorSubmissions,
    idealProfiles: adminData.idealProfiles,
    filteredBehavioralCount: adminData.filteredBehavioralCount,
    filteredWorkBehaviorCount: adminData.filteredWorkBehaviorCount,
    deletingProfileIds: adminData.deletingProfileIds,
    
    // Filters
    searchTerm: adminFilters.searchTerm,
    debouncedSearchTerm: adminFilters.debouncedSearchTerm,
    isSearching: adminFilters.isSearching || adminData.fetchingSearch,
    currentFilters: adminFilters.currentFilters,
    profileFilter: adminFilters.profileFilter,
    handleProfileFilterChange: adminFilters.handleProfileFilterChange,
    
    // Pagination
    behavioralPagination,
    workBehaviorPagination,
    profilesPagination,
    
    // Actions
    handleViewDetails: dashboardActions.handleViewDetails,
    handleSubmissionDeleted: dashboardActions.handleSubmissionDeleted,
    handleDeleteProfile: dashboardActions.handleDeleteProfile,
    handleFiltersChange: dashboardActions.handleFiltersChange,
    handleSearchChange: dashboardActions.handleSearchChange,
    handleSignOut: dashboardActions.handleSignOut,
    selectedSubmission: dashboardActions.selectedSubmission,
    isModalOpen: dashboardActions.isModalOpen,
    handleCloseModal: dashboardActions.handleCloseModal,
    
    // Utils
    getPageTitle,
    subscribed,
    handleManageSubscription,
    
    // Data operations
    updateSubmissionStatus: adminData.updateSubmissionStatus
  };
};
